/**
 * Sign-in helpers shared by the sign-in steps (routes/auth/components/signin-form.tsx) and the
 * passkey settings, kept out of React so they can be tested.
 */

export const CODE_LENGTH = 6;

/** What to tell someone when Better Auth refuses a step of sign-in. */
export function friendlyAuthError(error: { code?: string; message?: string; status?: number }) {
  switch (error.code) {
    case "INVALID_OTP":
      return "That code isn't right. Check it and try again.";
    case "OTP_EXPIRED":
      return "That code has expired. Send a new one.";
    case "TOO_MANY_ATTEMPTS":
      return "Too many attempts. Send a new code.";
    case "ACCOUNT_SUSPENDED":
      return (
        error.message ??
        "This account is suspended. If you think this is a mistake, write to us from its email address."
      );
  }
  if (error.status === 429) return "Too many requests. Wait a minute and try again.";
  return error.message ?? "Something went wrong. Try again.";
}

/** "pat.doe@x.com" -> "Pat Doe": a first guess for a new account's name. */
export function nameFromEmail(email: string) {
  return (email.split("@")[0] ?? "")
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** A readable name for a new passkey, from the user agent. */
export function deviceName(ua: string) {
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Mac/.test(ua)) return "Mac";
  if (/Android/.test(ua)) return "Android";
  if (/Windows/.test(ua)) return "Windows PC";
  return "This device";
}

/** Only digits, at most six: what a code field keeps of what was typed or pasted. */
export const codeDigits = (value: string) => value.replace(/\D/g, "").slice(0, CODE_LENGTH);

export const isEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value);
